/*
  tmc-patient-form.js — behaviour for snippets/tmc-patient-form.liquid. FROZEN: docs/form-contract.md §2.
  DOB auto-slash, "Other" reveal, validation, payload build, /cart.js read, /cart/add.js POST (one request,
  five properties on every test line, one satchel line if absent), cart-drawer refresh, error handling.
  One form per test (25 Sep 2026): with several tests selected the form is filled once per test, in turn,
  and each line carries its own five properties. Nothing is posted until every test has complete details.

  window.TMCPatientForm.mount(root, options)
    root                 the [data-tpf] element rendered by the snippet
    options.getSelected  () => [{ variantId, name, priceCents }]
    options.onSuccess    () => void, called after the drawer opens
    options.onEmpty      () => void, called when submit finds nothing selected (host closes its sheet)
  Returns { refresh() } — refresh() rebuilds the recap and the submit label from getSelected().
*/
(function () {
  'use strict';

  function money(cents) {
    var amount = cents / 100;
    var str = (amount % 1 === 0) ? amount.toFixed(0) : amount.toFixed(2);
    var parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function wrapperOf(el) {
    return el ? (el.closest ? el.closest('.tpf-field') : el.parentNode) : null;
  }
  function markInvalid(el) {
    if (!el) return;
    el.classList.add('is-invalid');
    var w = wrapperOf(el);
    if (w) { w.classList.add('is-invalid'); w.classList.remove('is-shaking'); void w.offsetWidth; w.classList.add('is-shaking'); }
  }
  function clearInvalid(el) {
    if (!el) return;
    el.classList.remove('is-invalid');
    var w = wrapperOf(el);
    if (w) w.classList.remove('is-invalid');
  }

  function mount(root, options) {
    if (!root || root.dataset.tpfReady === 'true') return root && root.tpf;
    root.dataset.tpfReady = 'true';
    options = options || {};
    var getSelected = options.getSelected || function () { return []; };
    var onSuccess = options.onSuccess || function () {};
    var onEmpty = options.onEmpty || function () {};

    var SHIP_FEE_VARIANT_ID = parseInt(root.dataset.tpfShipFeeVariantId, 10) || 47335571161237;
    var feeCents = parseInt(root.dataset.tpfFeeCents, 10) || 0;
    var feeLabel = root.dataset.tpfFeeLabel || 'Return satchel, once per order';
    var feeShown = root.dataset.tpfFeeAmount || money(feeCents);
    var submitTemplate = root.dataset.tpfSubmitLabel || 'Add {count} to cart · {total}';

    var fieldName = root.querySelector('[name="properties[Patient Name]"]');
    var fieldDob = root.querySelector('[name="properties[Patient DOB]"]');
    var fieldGender = root.querySelector('[name="properties[Patient Gender]"]');
    var fieldClinic = root.querySelector('[name="properties[Doctor Clinic]"]');
    var fieldClinicOther = root.querySelector('[data-tpf-other="clinic"]');
    var fieldDoc = root.querySelector('[name="properties[Doctor Name]"]');
    var fieldDocOther = root.querySelector('[data-tpf-other="doctor"]');
    var recap = root.querySelector('[data-tpf-recap]');
    var submitBtn = root.querySelector('[data-tpf-submit]');
    var submitLabel = root.querySelector('[data-tpf-submit-label]') || submitBtn;
    var errorLine = root.querySelector('[data-tpf-error]');
    var idleLabel = submitLabel ? submitLabel.textContent : '';

    /* 1. DOB: strip non-digits, cap at 8, insert slashes after 2 and 4. */
    if (fieldDob) fieldDob.addEventListener('input', function () {
      var v = this.value.replace(/\D/g, '').slice(0, 8);
      if (v.length >= 5) v = v.slice(0, 2) + '/' + v.slice(2, 4) + '/' + v.slice(4);
      else if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2, 4);
      this.value = v;
    });

    /* 2. "Other" reveals the sibling text input. */
    function wireOther(select, other) {
      if (!select || !other) return;
      select.addEventListener('change', function () {
        other.hidden = select.value !== '__other__';
        clearInvalid(select);
        if (other.hidden) clearInvalid(other);
        else other.focus();
      });
    }
    wireOther(fieldClinic, fieldClinicOther);
    wireOther(fieldDoc, fieldDocOther);

    /* 3. Any input/change clears the field's invalid state. */
    [fieldName, fieldDob, fieldClinicOther, fieldDocOther].forEach(function (el) {
      if (el) el.addEventListener('input', function () { clearInvalid(el); });
    });
    [fieldGender, fieldClinic, fieldDoc].forEach(function (el) {
      if (el) el.addEventListener('change', function () { clearInvalid(el); });
    });

    /* One form per test (Jackson, 25 Sep 2026). entries[i] holds the five properties for the i-th selected
       test; cursor is the test being filled. From the second test on, the person must pick "same details as"
       an earlier test or "enter new details" (sources[i]) before the fields show. Nothing is posted until
       every selected test has its own complete entry. */
    var stepBox = root.querySelector('[data-tpf-step]');
    var stepCount = root.querySelector('[data-tpf-step-count]');
    var stepName = root.querySelector('[data-tpf-step-name]');
    var choiceBox = root.querySelector('[data-tpf-choice]');
    var choiceList = root.querySelector('[data-tpf-choice-list]');
    var choiceError = root.querySelector('[data-tpf-choice-error]');
    var fieldsBox = root.querySelector('.tpf-fields');
    var backBtn = root.querySelector('[data-tpf-back]');
    var body = root.querySelector('.tpf__body');
    var uid = (fieldName && fieldName.id ? fieldName.id.replace(/^tpf-name-/, '') : 'tpf');
    var PROP_KEYS = ['Doctor Name', 'Doctor Clinic', 'Patient Name', 'Patient DOB', 'Patient Gender'];

    var entries = [];
    var sources = [];
    var cursor = 0;
    var signature = null;

    function selectedCents(items) {
      var sum = 0;
      items.forEach(function (i) { sum += i.priceCents || 0; });
      return sum;
    }
    function countLabel(n) { return n + (n === 1 ? ' test' : ' tests'); }

    function hasOption(select, value) {
      return !!select && Array.prototype.some.call(select.options, function (o) { return o.value === value && value !== '__other__' && value !== ''; });
    }
    function setPair(select, other, value) {
      if (!select) return;
      if (hasOption(select, value)) { select.value = value; if (other) { other.value = ''; other.hidden = true; } }
      else { select.value = '__other__'; if (other) { other.value = value; other.hidden = false; } }
    }
    function clearMarks() {
      Array.prototype.forEach.call(root.querySelectorAll('.is-invalid'), function (el) { el.classList.remove('is-invalid'); });
      if (choiceError) choiceError.hidden = true;
    }
    function writeFields(props) {
      clearMarks();
      if (fieldName) fieldName.value = props['Patient Name'];
      if (fieldDob) fieldDob.value = props['Patient DOB'];
      if (fieldGender) fieldGender.value = props['Patient Gender'];
      setPair(fieldClinic, fieldClinicOther, props['Doctor Clinic']);
      setPair(fieldDoc, fieldDocOther, props['Doctor Name']);
    }
    function clearFields() {
      clearMarks();
      if (fieldName) fieldName.value = '';
      if (fieldDob) fieldDob.value = '';
      if (fieldGender) fieldGender.value = '';
      setPair(fieldClinic, fieldClinicOther, 'Self-Referred');
      setPair(fieldDoc, fieldDocOther, 'Self-Referred');
    }
    function complete(props) {
      return !!props && PROP_KEYS.every(function (k) { return typeof props[k] === 'string' && props[k].trim() !== ''; });
    }

    /* A different set of tests (added, removed, reordered) starts the forms again from the first test. */
    function sync(items) {
      var sig = items.map(function (i) { return String(i.variantId); }).join(',');
      if (sig === signature) return;
      if (signature !== null) {
        var first = entries[0];
        entries = []; sources = []; cursor = 0;
        if (first) writeFields(first);
      }
      signature = sig;
      if (cursor > items.length - 1) cursor = Math.max(0, items.length - 1);
    }

    function renderChoice() {
      if (!choiceList) return;
      choiceList.textContent = '';
      var seen = {};
      var opts = [];
      for (var i = 0; i < cursor; i++) {
        var e = entries[i];
        if (!complete(e)) continue;
        var key = JSON.stringify(e);
        if (seen[key]) continue;
        seen[key] = true;
        opts.push({ value: 'copy:' + i, text: 'Same details as test ' + (i + 1) + ' (' + e['Patient Name'] + ')' });
      }
      opts.push({ value: 'new', text: 'Enter new details for this test' });
      opts.forEach(function (o) {
        var label = document.createElement('label');
        label.className = 'tpf-choice__opt';
        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'tpf-source-' + uid + '-' + cursor;
        input.value = o.value;
        input.setAttribute('data-tpf-source', '');
        input.checked = sources[cursor] === o.value;
        input.addEventListener('change', function () { choose(o.value); });
        var span = document.createElement('span');
        span.textContent = o.text;
        label.appendChild(input); label.appendChild(span);
        choiceList.appendChild(label);
      });
    }
    function choose(value) {
      sources[cursor] = value;
      if (choiceBox) choiceBox.classList.remove('is-invalid');
      if (value === 'new') clearFields();
      else writeFields(entries[parseInt(value.split(':')[1], 10)]);
      entries[cursor] = null;
      renderStep();
      if (fieldName) fieldName.focus();
    }

    function renderStep() {
      var items = getSelected();
      var n = items.length;
      var multi = n > 1;
      if (stepBox) stepBox.hidden = !multi;
      if (multi && stepCount) stepCount.textContent = 'Test ' + (cursor + 1) + ' of ' + n;
      if (multi && stepName) stepName.textContent = items[cursor] ? items[cursor].name : '';
      var needsChoice = multi && cursor > 0;
      if (choiceBox) { choiceBox.hidden = !needsChoice; if (needsChoice) renderChoice(); }
      if (fieldsBox) fieldsBox.hidden = needsChoice && !sources[cursor];
      if (backBtn) {
        backBtn.hidden = !needsChoice;
        backBtn.textContent = needsChoice ? 'Back to test ' + cursor : '';
      }
      var total = n ? selectedCents(items) + feeCents : 0;
      idleLabel = submitTemplate.replace('{count}', countLabel(n)).replace('{total}', money(total));
      var label = (multi && cursor < n - 1) ? 'Next: test ' + (cursor + 2) + ' of ' + n : idleLabel;
      if (submitLabel && !submitBtn.disabled) submitLabel.textContent = label;
    }

    function refresh() {
      var items = getSelected();
      sync(items);
      var total = items.length ? selectedCents(items) + feeCents : 0;
      if (recap) {
        recap.textContent = '';
        items.forEach(function (i, idx) {
          var row = recapRow(i.name, money(i.priceCents || 0), 'tpf-recap__row');
          if (items.length > 1 && idx === cursor) row.classList.add('is-current');
          if (items.length > 1 && complete(entries[idx])) {
            var who = document.createElement('span');
            who.className = 'tpf-recap__who';
            who.textContent = 'For ' + entries[idx]['Patient Name'];
            row.firstChild.appendChild(who);
          }
          recap.appendChild(row);
        });
        if (items.length) recap.appendChild(recapRow(feeLabel, feeShown, 'tpf-recap__row tpf-recap__row--fee'));
        recap.appendChild(recapRow('Total', money(total), 'tpf-recap__row tpf-recap__row--total'));
      }
      renderStep();
    }
    function recapRow(name, price, cls) {
      var row = document.createElement('div');
      row.className = cls;
      var a = document.createElement('span'); a.textContent = name;
      var b = document.createElement('span'); b.textContent = price;
      row.appendChild(a); row.appendChild(b);
      return row;
    }

    function goTo(index) {
      cursor = index;
      if (entries[cursor]) writeFields(entries[cursor]);
      else if (cursor > 0) { clearFields(); sources[cursor] = undefined; }
      refresh();
      if (body) body.scrollTop = 0;
      var target = (cursor > 0 && !sources[cursor]) ? root.querySelector('[data-tpf-source]') : fieldName;
      if (target) target.focus();
    }
    if (backBtn) backBtn.addEventListener('click', function () {
      if (cursor === 0) return;
      /* Keep what was typed on this test if it is already valid, so going back and forward loses nothing. */
      var here = collect(true);
      if (here && sources[cursor]) entries[cursor] = here;
      goTo(cursor - 1);
    });

    function setBusy(busy) {
      submitBtn.disabled = busy;
      submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (submitLabel) submitLabel.textContent = busy ? 'Adding…' : idleLabel;
    }
    function showError(show) {
      if (errorLine) errorLine.hidden = !show;
    }

    /* Validate the five fields; return the properties object, or null (fields marked unless quiet). */
    function collect(quiet) {
      var mark = quiet ? function () {} : markInvalid;
      var valid = true;
      if (!fieldName || !fieldName.value.trim()) { mark(fieldName); valid = false; }
      if (!fieldDob || !fieldDob.value.trim()) { mark(fieldDob); valid = false; }
      if (!fieldGender || !fieldGender.value) { mark(fieldGender); valid = false; }

      var clinicValue = '';
      if (fieldClinic) {
        if (fieldClinic.value === '__other__') {
          clinicValue = fieldClinicOther ? fieldClinicOther.value.trim() : '';
          if (!clinicValue) { mark(fieldClinicOther); mark(fieldClinic); valid = false; }
        } else if (!fieldClinic.value) { mark(fieldClinic); valid = false; }
        else { clinicValue = fieldClinic.value; }
      }
      var docValue = '';
      if (fieldDoc) {
        if (fieldDoc.value === '__other__') {
          docValue = fieldDocOther ? fieldDocOther.value.trim() : '';
          if (!docValue) { mark(fieldDocOther); mark(fieldDoc); valid = false; }
        } else if (!fieldDoc.value) { mark(fieldDoc); valid = false; }
        else { docValue = fieldDoc.value; }
      }
      if (!valid) return null;
      return {
        'Doctor Name': docValue,
        'Doctor Clinic': clinicValue,
        'Patient Name': fieldName ? fieldName.value.trim() : '',
        'Patient DOB': fieldDob ? fieldDob.value.trim() : '',
        'Patient Gender': fieldGender ? fieldGender.value : ''
      };
    }

    /* 4–11. Validate this test's details; move to the next test, or, on the last, check every test has its
       own complete details, add everything in one request and open the drawer. */
    async function validateAndAdd() {
      showError(false);
      var selected = getSelected();
      if (selected.length === 0) { onEmpty(); return; }
      sync(selected);

      if (selected.length > 1 && cursor > 0 && !sources[cursor]) {
        if (choiceBox) choiceBox.classList.add('is-invalid');
        if (choiceError) choiceError.hidden = false;
        if (choiceBox.scrollIntoView) choiceBox.scrollIntoView({ block: 'nearest' });
        var first = root.querySelector('[data-tpf-source]');
        if (first) first.focus();
        return;
      }
      var props = collect(false);
      if (!props) return;
      entries[cursor] = props;

      if (cursor < selected.length - 1) { goTo(cursor + 1); return; }

      /* The guarantee: one complete set of details per selected test, or nothing is sent. */
      for (var i = 0; i < selected.length; i++) {
        if (!complete(entries[i])) { goTo(i); showError(true); return; }
      }

      setBusy(true);

      var items = selected.map(function (s, idx) {
        var own = {};
        PROP_KEYS.forEach(function (k) { own[k] = entries[idx][k]; });
        return { id: parseInt(s.variantId, 10), quantity: 1, properties: own };
      });

      try {
        var cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        var cart = await cartRes.json();
        var hasShipFee = !!(cart.items && cart.items.some(function (item) { return item.variant_id === SHIP_FEE_VARIANT_ID; }));
        if (!hasShipFee) items.push({ id: SHIP_FEE_VARIANT_ID, quantity: 1 });

        var addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
        });
        if (!addRes.ok) throw new Error('Cart add failed: ' + addRes.status);
        var json = await addRes.json();

        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(json);
        else { window.location.href = '/cart'; return; }
        setBusy(false);
        entries = []; sources = []; cursor = 0; signature = null;
        onSuccess();
      } catch (err) {
        console.error('Add to cart error:', err);
        setBusy(false);
        showError(true);
      }
    }

    if (submitBtn) submitBtn.addEventListener('click', function () { validateAndAdd(); });

    root.tpf = { refresh: refresh, submit: validateAndAdd, money: money };
    refresh();
    return root.tpf;
  }

  window.TMCPatientForm = { mount: mount, money: money };
})();
